import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Menu, type MenuAction } from "../../components/Menu";
import { useAuth } from "../../auth/AuthProvider";
import { useToast } from "../../components/Toast";

/**
 * الإجراءات السريعة على العقد — قائمةٌ لا أزرارٌ مبعثرة في الصفّ.
 *
 * معاينة، تفعيل (للمسوّدة)، إنهاء وإلغاء، وحذف للمدير. وما لا يصلح
 * للحالة يُعرض معطّلًا بسببه.
 *
 * والإنهاءُ غيرُ الإلغاء: الإنهاءُ شهادةٌ أنّ العقد جرى حتى تمّ، والإلغاءُ
 * شهادةٌ أنّه لم يجرِ. وكلاهما غيرُ الحذف — الحذفُ يمحو السجلَّ فلا يبقى
 * ما يُراجَع، والملغيُّ يبقى يُقرأ ويُعرف متى أُلغي.
 */
export function ContractActions({
  contract,
  onPreview,
  onChanged,
  fromAccount = false,
}: {
  contract: {
    id: string;
    number: string;
    status: string;
    signedAt?: string | null;
  };
  onPreview: () => void;
  onChanged: () => void;
  /** فُتحت القائمة من ملف العميل — «فتح» يحمل أثر العودة إليه. */
  fromAccount?: boolean;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();

  const done = (msg: string) => {
    toast(msg);
    void queryClient.invalidateQueries({ queryKey: ["contracts"] });
    void queryClient.invalidateQueries({ queryKey: ["account"] });
    void queryClient.invalidateQueries({ queryKey: ["quotations"] });
    void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
    onChanged();
  };

  const setStatus = useMutation({
    mutationFn: (status: "ACTIVE" | "COMPLETED") =>
      api(`/sales/contracts/${contract.id}`, {
        method: "PATCH",
        body: { status },
      }),
    onSuccess: (_r, status) =>
      done(status === "ACTIVE" ? "فُعّل العقد" : "أُنهي العقد"),
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ"),
  });

  const cancel = useMutation({
    mutationFn: () =>
      api(`/sales/contracts/${contract.id}/cancel`, { method: "POST" }),
    onSuccess: () => done("أُلغي العقد — يبقى في السجلّ ملغًى"),
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الإلغاء"),
  });

  const remove = useMutation({
    mutationFn: () =>
      api(`/sales/contracts/${contract.id}`, { method: "DELETE" }),
    onSuccess: () => done("حُذف العقد"),
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحذف"),
  });

  /* الختم من الصفّ — والخادم يحكم: لا توقيعَ محفوظًا؟ مختومٌ من قبل؟ يقولها. */
  const sign = useMutation({
    mutationFn: () =>
      api(`/sales/contracts/${contract.id}/sign`, { method: "POST" }),
    onSuccess: () => done("خُتم العقد بتوقيع الشركة"),
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التوقيع"),
  });

  const isDraft = contract.status === "DRAFT";
  const isActive = contract.status === "ACTIVE";
  const signed = Boolean(contract.signedAt);
  /* ما لم يجرِ بعدُ أو ما زال جاريًا يُلغى؛ والمنتهي والملغيُّ لا. */
  const canCancel = isDraft || isActive;

  const actions: MenuAction[] = [
    { key: "preview", label: "معاينة", icon: "search", onSelect: onPreview },
    {
      key: "open",
      label: "فتح وتعديل",
      icon: "edit",
      onSelect: () =>
        navigate(
          `/sales/contracts/${contract.id}${fromAccount ? "?from=account" : ""}`,
        ),
    },
    {
      key: "sign",
      label: "توقيع بختم الشركة",
      icon: "edit",
      onSelect: () => sign.mutate(),
      disabled: signed || contract.status === "CANCELLED" || sign.isPending,
      title: signed
        ? "مختومٌ بالفعل"
        : contract.status === "CANCELLED"
          ? "لا يُوقَّع عقدٌ ملغي"
          : undefined,
      separated: true,
    },
    {
      key: "activate",
      label: "تفعيل",
      icon: "check",
      onSelect: () => setStatus.mutate("ACTIVE"),
      disabled: !isDraft,
      title: isDraft ? undefined : "يُفعَّل من المسوّدة فقط",
    },
    {
      key: "complete",
      label: "إنهاء",
      icon: "clock",
      onSelect: () => setStatus.mutate("COMPLETED"),
      disabled: !isActive,
      title: isActive
        ? "العقد جرى حتى تمّ — يُقفل منتهيًا"
        : "يُنهى العقد الساري فقط",
    },
    {
      key: "cancel",
      label: "إلغاء",
      icon: "x",
      onSelect: () => {
        if (
          window.confirm(
            `إلغاء العقد ${contract.number}؟ يُسجَّل كأنّه لم يجرِ، ويبقى في السجلّ للمراجعة.`,
          )
        )
          cancel.mutate();
      },
      disabled: !canCancel || cancel.isPending,
      title: canCancel
        ? "العقد لم يجرِ — يُسجَّل كأن لم يكن"
        : contract.status === "CANCELLED"
          ? "ملغًى بالفعل"
          : "المنتهي لا يُلغى — قد جرى وانقضى",
      danger: true,
    },
  ];

  if (atLeast("MANAGER")) {
    actions.push({
      key: "delete",
      label: "حذف",
      icon: "trash",
      onSelect: () => {
        if (window.confirm(`حذف العقد ${contract.number}؟`)) remove.mutate();
      },
      danger: true,
      separated: true,
    });
  }

  return <Menu actions={actions} label={`إجراءات العقد ${contract.number}`} />;
}
